import { cn } from '@/lib/utils';
import { safeFormat } from '@/lib/safeDate';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { getAvatarColor } from '@/lib/utils';
import { format } from 'date-fns';

import { DEMAND_TYPE_CONFIG } from '@/hooks/useSupportInbox';
import type { SessionWithSla, DemandType } from '@/types/support.types';
import { Zap, Tag, CheckCheck, AlertTriangle } from 'lucide-react';

interface SessionCardProps {
  session: SessionWithSla;
  isSelected: boolean;
  hasOpenEscalation?: boolean;
  onClick: () => void;
}

export function SessionCard({ session, isSelected, hasOpenEscalation, onClick }: SessionCardProps) {
  const contactName =
    [session.contact_first_name, session.contact_last_name].filter(Boolean).join(' ') ||
    session.contact_name ||
    'Sem nome';
  const hasUnread = (session.unread_count || 0) > 0;
  const demandConfig = session.demand_type
    ? DEMAND_TYPE_CONFIG[session.demand_type as DemandType]
    : null;
  const agentName = session.agent_name;
  const siblingCount = session.sibling_count || 0;
  const followUpCount = session.follow_up_count || 0;
  const isPreTicket = session.status === 'pre_ticket';
  const lastMessageDirection = (session as any).last_message_direction as
    | 'inbound'
    | 'outbound'
    | undefined;
  const preview =
    (session as any).last_message_preview ||
    session.pending_action ||
    '';
  const time = session.updated_at ? safeFormat(new Date(session.updated_at), 'HH:mm') : '';

  return (
    <div
      className={cn(
        'pl-3 pr-3 py-2.5 cursor-pointer hover:bg-wa-sidebar-active transition-colors group relative touch-action-manipulation',
        isSelected && 'bg-wa-sidebar-active',
        isPreTicket && 'border-l-[3px] border-l-indigo-400'
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <Avatar className="h-12 w-12 flex-shrink-0">
          <AvatarFallback
            className={cn(
              'bg-gradient-to-br text-white text-base',
              getAvatarColor(contactName)
            )}
          >
            {contactName.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0 border-b border-wa-divider pb-2 -mb-0.5">
          {/* Linha 1: nome + horário */}
          <div className="flex items-center justify-between mb-0.5 gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <h3
                className={cn(
                  'truncate text-[15px] text-wa-text',
                  hasUnread ? 'font-semibold' : 'font-normal'
                )}
              >
                {contactName}
              </h3>
            </div>
            {time && (
              <span
                className={cn(
                  'text-[12px] flex-shrink-0',
                  hasUnread ? 'text-wa-green font-medium' : 'text-wa-text-muted'
                )}
              >
                {time}
              </span>
            )}
          </div>

          {/* Linha 2: prévia (somente quando existe) */}
          {preview && (
            <div
              className={cn(
                'flex items-center gap-1 min-w-0 text-[13px]',
                hasUnread ? 'text-wa-text font-medium' : 'text-wa-text-muted'
              )}
            >
              {lastMessageDirection === 'outbound' && (
                <CheckCheck className="w-3.5 h-3.5 text-wa-text-muted shrink-0 [&>path]:stroke-[2.5]" />
              )}
              <span className="truncate">{preview}</span>
            </div>
          )}

          {/* Linha 3: telefone + agente + siblings + badge unread */}
          <div className="flex items-center justify-between gap-2">
            <p className="text-[12px] text-wa-text-muted truncate min-w-0 flex-1">
              {session.contact_phone || ''}
              {agentName && (
                <span className="ml-1.5 text-primary/80">• {agentName}</span>
              )}
            </p>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {siblingCount > 1 && (
                <span className="text-[10px] text-wa-text-muted bg-muted px-1.5 py-0.5 rounded">
                  {siblingCount} chamados
                </span>
              )}
              {hasUnread && (
                <Badge className="bg-wa-green hover:bg-wa-green text-white border-0 rounded-full h-5 min-w-5 flex items-center justify-center px-1.5 text-[11px] font-semibold">
                  {session.unread_count}
                </Badge>
              )}
            </div>
          </div>

          {/* Linha 4: badges */}
          {(isPreTicket || demandConfig || followUpCount > 0 || hasOpenEscalation) && (
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {hasOpenEscalation && (
                <Badge
                  variant="outline"
                  className="text-[10px] px-1.5 py-0 h-5 border-amber-500/40 text-amber-700 dark:text-amber-400"
                >
                  <AlertTriangle className="h-3 w-3 mr-0.5" />
                  Escalação em aberto
                </Badge>
              )}
              {isPreTicket && (
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 h-5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
                >
                  <Tag className="h-3 w-3 mr-0.5" />
                  Pré-chamado
                </Badge>
              )}
              {demandConfig && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
                  {demandConfig.label}
                </Badge>
              )}
              {followUpCount > 0 && (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-5">
                  <Zap className="h-3 w-3 mr-0.5" />
                  {followUpCount}ª cobrança
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
